import { useMemo } from 'react';

import { c } from 'ttag';

import { useAddresses } from '@proton/account/addresses/hooks';
import { useUserKeys } from '@proton/account/userKeys/hooks';
import { Button } from '@proton/atoms/Button/Button';
import { useContact } from '@proton/mail/store/contacts/contactHooks';
import { useContactGroups } from '@proton/mail/store/labels/hooks';
import { toMap } from '@proton/shared/lib/helpers/object';
import type { ContactEmail } from '@proton/shared/lib/interfaces/contacts';
import noop from '@proton/utils/noop';

import Loader from '../../../components/loader/Loader';
import type { ModalProps } from '../../../components/modalTwo/Modal';
import ModalTwo from '../../../components/modalTwo/Modal';
import ModalTwoContent from '../../../components/modalTwo/ModalContent';
import ModalTwoFooter from '../../../components/modalTwo/ModalFooter';
import ModalTwoHeader from '../../../components/modalTwo/ModalHeader';
import useContactList from '../hooks/useContactList';
import useVCardContact from '../hooks/useVCardContact';
import ContactView from '../view/ContactView';

interface ContactMergeDetailsModalProps {
    contactID: string;
}

type Props = ContactMergeDetailsModalProps & ModalProps;

const ContactMergeDetailsModal = ({ contactID, ...rest }: Props) => {
    const [userKeysList] = useUserKeys();
    const [contact] = useContact(contactID);
    const { vCardContact, isLoading, errors } = useVCardContact({ contact, userKeysList });
    const { loading: loadingContactEmails, contactEmailsMap } = useContactList({});

    const [addresses = [], loadingAddresses] = useAddresses();
    const ownAddresses = useMemo(() => addresses.map(({ Email }) => Email), [addresses]);

    const [contactGroups = [], loadingContactGroups] = useContactGroups();
    const contactGroupsMap = useMemo(() => toMap(contactGroups), [contactGroups]);

    if (!isLoading && !vCardContact && vCardContact === undefined) {
        return <>{null}</>;
    }

    return (
        <ModalTwo size="large" className="contacts-modal" {...rest}>
            <ModalTwoHeader title={c('Title').t`Contact Details`} />
            <ModalTwoContent>
                {isLoading || loadingContactEmails || loadingAddresses || loadingContactGroups ? (
                    <Loader />
                ) : (
                    <ContactView
                        vCardContact={vCardContact!}
                        errors={errors}
                        contactID={contactID}
                        isPreview
                        contactEmails={contactEmailsMap[contactID] as ContactEmail[]}
                        contactGroupsMap={contactGroupsMap}
                        ownAddresses={ownAddresses}
                        onReload={noop}
                        onEdit={noop}
                        onEmailSettings={noop}
                        onGroupDetails={noop}
                        onGroupEdit={noop}
                        onUpgrade={noop}
                        onSignatureError={noop}
                        onDecryptionError={noop}
                    />
                )}
            </ModalTwoContent>
            <ModalTwoFooter>
                <Button color="norm" className="ml-auto" onClick={rest.onClose}>{c('Action').t`Close`}</Button>
            </ModalTwoFooter>
        </ModalTwo>
    );
};

export default ContactMergeDetailsModal;
